import { AlertTriangle, Calendar, PartyPopper } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import type { EnrichedDayStats } from "@/lib/reviews/day-statistics-enricher";
import { WeatherForecastLine } from "./WeatherForecastLine";
import { CompactShiftStatusBadge } from '@/components/shifts/status/ShiftStatusBadge';
import { ShiftStatus } from '@/lib/scheduling/shift-status-types';

interface DayStatsBadgesProps {
  stats: EnrichedDayStats;
}

/**
 * Display inline badges for day statistics
 * Shows weekend, holiday, weather, and issue indicators
 */
export function DayStatsBadges({ stats }: DayStatsBadgesProps) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      {/* Weather Forecast Line - displays temperature gradient and icon */}
      {stats.weather ? (
        <WeatherForecastLine weather={stats.weather} />
      ) : (
        <div className="text-xs text-muted-foreground">No weather data</div>
      )}

      {/* Weekend Badge */}
      {stats.isWeekend && (
        <Badge variant="outline" className="flex items-center gap-1">
          <Calendar className="h-3 w-3" />
          <span>Weekend</span>
        </Badge>
      )}

      {/* Holiday Badge */}
      {stats.isHoliday && (
        <Badge
          variant="outline"
          className="flex items-center gap-1 bg-primary/10 border-primary/30 text-primary"
        >
          <PartyPopper className="h-3 w-3" />
          <span>{stats.holidayName || 'Holiday'}</span>
        </Badge>
      )}

      {/* Shift Status Badges - using same badges as Time Tracking tab */}
      {stats.shiftStatusBreakdown && (
        <>
          {/* Missed shifts */}
          {stats.shiftStatusBreakdown.missed > 0 && (
            <div className="flex items-center gap-1">
              {stats.shiftStatusBreakdown.missed > 1 && (
                <span className="text-xs font-medium text-muted-foreground">{stats.shiftStatusBreakdown.missed}x</span>
              )}
              <CompactShiftStatusBadge
                status={ShiftStatus.MISSED}
                showTooltip={true}
              />
            </div>
          )}

          {/* Late shifts */}
          {stats.shiftStatusBreakdown.late > 0 && (
            <div className="flex items-center gap-1">
              {stats.shiftStatusBreakdown.late > 1 && (
                <span className="text-xs font-medium text-muted-foreground">{stats.shiftStatusBreakdown.late}x</span>
              )}
              <CompactShiftStatusBadge
                status={ShiftStatus.LATE}
                showTooltip={true}
              />
            </div>
          )}

          {/* Completed late shifts */}
          {stats.shiftStatusBreakdown.completedLate > 0 && (
            <div className="flex items-center gap-1">
              {stats.shiftStatusBreakdown.completedLate > 1 && (
                <span className="text-xs font-medium text-muted-foreground">{stats.shiftStatusBreakdown.completedLate}x</span>
              )}
              <CompactShiftStatusBadge
                status={ShiftStatus.COMPLETED_LATE}
                showTooltip={true}
              />
            </div>
          )}

          {/* Unassigned shifts */}
          {stats.shiftStatusBreakdown.unassigned > 0 && (
            <div className="flex items-center gap-1">
              {stats.shiftStatusBreakdown.unassigned > 1 && (
                <span className="text-xs font-medium text-muted-foreground">{stats.shiftStatusBreakdown.unassigned}x</span>
              )}
              <CompactShiftStatusBadge
                status={ShiftStatus.UNASSIGNED}
                showTooltip={true}
              />
            </div>
          )}

          {/* Pending shifts */}
          {stats.shiftStatusBreakdown.pending > 0 && (
            <div className="flex items-center gap-1">
              {stats.shiftStatusBreakdown.pending > 1 && (
                <span className="text-xs font-medium text-muted-foreground">{stats.shiftStatusBreakdown.pending}x</span>
              )}
              <CompactShiftStatusBadge
                status={ShiftStatus.PENDING}
                showTooltip={true}
              />
            </div>
          )}

          {/* In progress shifts */}
          {stats.shiftStatusBreakdown.inProgress > 0 && (
            <div className="flex items-center gap-1">
              {stats.shiftStatusBreakdown.inProgress > 1 && (
                <span className="text-xs font-medium text-muted-foreground">{stats.shiftStatusBreakdown.inProgress}x</span>
              )}
              <CompactShiftStatusBadge
                status={ShiftStatus.IN_PROGRESS}
                showTooltip={true}
              />
            </div>
          )}

          {/* Completed shifts */}
          {stats.shiftStatusBreakdown.completed > 0 && (
            <div className="flex items-center gap-1">
              {stats.shiftStatusBreakdown.completed > 1 && (
                <span className="text-xs font-medium text-muted-foreground">{stats.shiftStatusBreakdown.completed}x</span>
              )}
              <CompactShiftStatusBadge
                status={ShiftStatus.COMPLETED}
                showTooltip={true}
              />
            </div>
          )}

          {/* On time shifts */}
          {stats.shiftStatusBreakdown.onTime > 0 && (
            <div className="flex items-center gap-1">
              {stats.shiftStatusBreakdown.onTime > 1 && (
                <span className="text-xs font-medium text-muted-foreground">{stats.shiftStatusBreakdown.onTime}x</span>
              )}
              <CompactShiftStatusBadge
                status={ShiftStatus.ON_TIME}
                showTooltip={true}
              />
            </div>
          )}
        </>
      )}

      {/* Coverage Gap Badge */}
      {!stats.openingHoursCovered && stats.coverageGaps && stats.coverageGaps.length > 0 && (
        <Badge
          variant="outline"
          className="flex items-center gap-1 bg-orange-50 dark:bg-orange-900/30 border-orange-300 dark:border-orange-700 text-orange-800 dark:text-orange-300"
        >
          <AlertTriangle className="h-3 w-3" />
          <span>{stats.coverageGaps.length} coverage gap{stats.coverageGaps.length !== 1 ? 's' : ''}</span>
        </Badge>
      )}
    </div>
  );
}
